import React, { useState } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { Spinner } from "@/components/Atoms";
import { fmtRel } from "@/lib/format";
import { Activity, Zap, Layers, ChevronUp, ChevronDown, Server, SlidersHorizontal, History } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

const DEF = { scale_up_cpu: 75, scale_up_ram: 80, scale_down_cpu: 25, scale_down_ram: 40, samples_up: 3, samples_down: 5, max_stage: 5, cooldown_seconds: 300 };

export default function LiveMetricsAutoscalingPanel({
    cloudId, currentStage, policy, ncStatus, instances = [], agentsLive = [], onChange,
}) {
    const { data: metrics, mutate: mutateMetrics } = useSWR(
        `/api/admin/clouds/${cloudId}/metrics?samples=20`, fetcher,
        { refreshInterval: 5000 },
    );
    const { data: stages } = useSWR("/api/admin/autoscaling/stages", fetcher);
    const { data: history, mutate: mutateHistory } = useSWR(
        `/api/admin/clouds/${cloudId}/scale-history?limit=8`, fetcher,
        { refreshInterval: 15000 },
    );
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [editing, setEditing] = useState(false);
    const eff = (k) => (policy && policy[k] != null ? policy[k] : DEF[k]);
    const [form, setForm] = useState(null);

    const latest = (metrics && metrics[metrics.length - 1]) || {};
    const si = ncStatus?.serverinfo || null;

    const webCount = instances.filter((i) => i.role === "web").length;
    const workerCount = instances.filter((i) => i.role === "worker").length;

    const triggerSpike = async () => {
        await api.post(`/api/admin/clouds/${cloudId}/spike?duration_seconds=180`);
        mutateMetrics();
        onChange && onChange();
    };
    const scaleTo = async (target) => {
        setBusy(true); setErr(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/scale`, { target_stage: target });
            onChange && onChange(); mutateHistory();
        } catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    };
    const toggleEnabled = async (enabled) => {
        setBusy(true); setErr(null);
        try {
            await api.patch(`/api/admin/clouds/${cloudId}/autoscaling`, { enabled });
            onChange && onChange();
        } catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    };
    const openEditor = () => {
        setForm({
            scale_up_cpu: eff("scale_up_cpu"), scale_up_ram: eff("scale_up_ram"),
            scale_down_cpu: eff("scale_down_cpu"), scale_down_ram: eff("scale_down_ram"),
            samples_up: eff("samples_up"), samples_down: eff("samples_down"),
            max_stage: eff("max_stage"),
            cooldown_min: Math.round(eff("cooldown_seconds") / 60),
        });
        setEditing(true);
    };
    const saveThresholds = async () => {
        setBusy(true); setErr(null);
        try {
            await api.patch(`/api/admin/clouds/${cloudId}/autoscaling`, {
                scale_up_cpu: Number(form.scale_up_cpu),
                scale_up_ram: Number(form.scale_up_ram),
                scale_down_cpu: Number(form.scale_down_cpu),
                scale_down_ram: Number(form.scale_down_ram),
                samples_up: Number(form.samples_up),
                samples_down: Number(form.samples_down),
                max_stage: Number(form.max_stage),
                cooldown_seconds: Math.max(30, Number(form.cooldown_min) * 60),
            });
            setEditing(false);
            onChange && onChange();
        } catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    };

    const memPct = (si && si.mem_total_kb)
        ? Math.max(0, Math.min(100, (1 - (si.mem_free_kb || 0) / si.mem_total_kb) * 100))
        : null;
    const load1 = Array.isArray(si?.cpuload) ? si.cpuload[0] : null;

    return (
        <div className="cm-card p-5" data-testid="metrics-autoscaling-panel">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Activity size={14} className="text-zinc-400" />
                    <div className="cm-h3">Live Metrics &amp; Autoscaling</div>
                </div>
                <button
                    onClick={triggerSpike}
                    className="cm-btn-secondary text-xs flex items-center gap-1.5"
                    data-testid="metrics-spike-button"
                    title="Force CPU/RAM > 80% for 3 min (autoscaling demo)"
                >
                    <Zap size={11} /> Simulate load spike
                </button>
            </div>

            {/* --- Live (aggregierte Cloud-Metriken) --- */}
            <div className="grid grid-cols-3 gap-px bg-zinc-800 mb-3">
                <Gauge label="CPU" value={latest.cpu_pct ?? 0} unit="%" danger={(latest.cpu_pct ?? 0) > 75} />
                <Gauge label="RAM" value={latest.ram_pct ?? 0} unit="%" danger={(latest.ram_pct ?? 0) > 80} />
                <Gauge label="REQ" value={latest.req_rate ?? 0} unit="/s" danger={(latest.req_rate ?? 0) > 250} />
            </div>
            <Sparkline data={metrics} field="cpu_pct" label="CPU %" />
            <Sparkline data={metrics} field="ram_pct" label="RAM %" />
            <Sparkline data={metrics} field="req_rate" label="req/s" />

            {/* --- Per-VM Live-Telemetrie (echte Agent-Heartbeats) --- */}
            {agentsLive.length > 0 && (
                <div className="mt-3 pt-3 border-t border-zinc-800" data-testid="per-vm-metrics">
                    <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5">
                        Per VM (agent heartbeat)
                    </div>
                    <div className="space-y-1">
                        {agentsLive.map((a, idx) => (
                            <div
                                key={idx}
                                data-testid={`per-vm-row-${a.role}-${idx}`}
                                className="flex items-center justify-between bg-zinc-950 border border-zinc-800 px-2 py-1 text-xs"
                            >
                                <span className="font-mono text-zinc-300">
                                    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${a.status === "online" ? "bg-emerald-400" : "bg-zinc-600"}`} />
                                    {a.role}{a.hostname ? ` · ${a.hostname}` : ""}
                                </span>
                                <span className="font-mono text-zinc-400">
                                    CPU <span className={(a.cpu_pct ?? 0) > 75 ? "text-red-400" : "text-zinc-200"}>{a.cpu_pct != null ? `${a.cpu_pct}%` : "—"}</span>
                                    {"  "}· RAM <span className={(a.ram_pct ?? 0) > 80 ? "text-red-400" : "text-zinc-200"}>{a.ram_pct != null ? `${a.ram_pct}%` : "—"}</span>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* --- Nextcloud serverinfo (echte NC-Telemetrie) --- */}
            <div className="mt-3 pt-3 border-t border-zinc-800" data-testid="serverinfo-telemetry">
                <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] uppercase tracking-wide text-zinc-500 flex items-center gap-1">
                        <Server size={11} /> Nextcloud (serverinfo)
                    </span>
                    <span className="font-mono text-[11px] text-zinc-600">
                        Web {webCount} · Worker {workerCount}
                    </span>
                </div>
                {si ? (
                    <div className="grid grid-cols-2 gap-x-4">
                        <Row k="CPU load (1m)" v={load1 != null ? Number(load1).toFixed(2) : "—"} testid="si-load" />
                        <Row k="RAM used" v={memPct != null ? `${memPct.toFixed(0)} %` : "—"} testid="si-ram" />
                        <Row k="Active users (24h)" v={si.active_users_last24h ?? "—"} testid="si-active" />
                        <Row k="App updates" v={si.apps_updates_available ?? "—"} testid="si-appupd" />
                    </div>
                ) : (
                    <p className="text-xs text-zinc-600" data-testid="serverinfo-missing">
                        No serverinfo data — set the token on the cloud (Live status box).
                    </p>
                )}
            </div>

            {/* --- Autoscaling --- */}
            <div className="mt-3 pt-3 border-t border-zinc-800">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <Layers size={13} className="text-zinc-400" />
                        <div className="text-xs font-semibold text-zinc-300">Autoscaling</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            data-testid="autoscale-edit-toggle"
                            onClick={() => (editing ? setEditing(false) : openEditor())}
                            className="text-[11px] font-mono text-zinc-500 hover:text-zinc-200 flex items-center gap-1"
                            title="Adjust thresholds for this cloud"
                        >
                            <SlidersHorizontal size={10} /> Rules
                        </button>
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid="autoscaling-enabled-toggle"
                                checked={!!policy?.enabled}
                                onChange={(e) => toggleEnabled(e.target.checked)}
                                disabled={busy}
                            />
                            <span className="font-mono uppercase text-[11px] tracking-widest text-zinc-400">auto enabled</span>
                        </label>
                    </div>
                </div>
                {err && <div className="text-xs text-cm-danger mb-2" data-testid="autoscale-error">{err}</div>}

                {editing && form ? (
                    <div className="bg-zinc-950 border border-zinc-800 p-3 mb-2 space-y-2" data-testid="autoscale-editor">
                        <div className="grid grid-cols-2 gap-2">
                            <NumField label="↑ CPU %" testid="f-up-cpu" value={form.scale_up_cpu} onChange={(v) => setForm({ ...form, scale_up_cpu: v })} />
                            <NumField label="↑ RAM %" testid="f-up-ram" value={form.scale_up_ram} onChange={(v) => setForm({ ...form, scale_up_ram: v })} />
                            <NumField label="↓ CPU %" testid="f-down-cpu" value={form.scale_down_cpu} onChange={(v) => setForm({ ...form, scale_down_cpu: v })} />
                            <NumField label="↓ RAM %" testid="f-down-ram" value={form.scale_down_ram} onChange={(v) => setForm({ ...form, scale_down_ram: v })} />
                            <NumField label="Samples ↑" testid="f-samples-up" value={form.samples_up} onChange={(v) => setForm({ ...form, samples_up: v })} />
                            <NumField label="Samples ↓" testid="f-samples-down" value={form.samples_down} onChange={(v) => setForm({ ...form, samples_down: v })} />
                            <NumField label="max Stage" testid="f-max-stage" value={form.max_stage} onChange={(v) => setForm({ ...form, max_stage: v })} />
                            <NumField label="Cooldown (min)" testid="f-cooldown" value={form.cooldown_min} onChange={(v) => setForm({ ...form, cooldown_min: v })} />
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                            <button onClick={saveThresholds} disabled={busy} data-testid="autoscale-save-thresholds" className="cm-btn-primary text-xs disabled:opacity-40">Save</button>
                            <button onClick={() => setEditing(false)} className="cm-btn-ghost text-xs">Cancel</button>
                        </div>
                    </div>
                ) : (
                    <div className="text-[11px] text-zinc-600 mb-2 font-mono" data-testid="autoscale-rule-caption">
                        Auto: ↑ when CPU&gt;{eff("scale_up_cpu")}% OR RAM&gt;{eff("scale_up_ram")}% ({eff("samples_up")}×),
                        {" "}↓ when CPU&lt;{eff("scale_down_cpu")}% AND RAM&lt;{eff("scale_down_ram")}% ({eff("samples_down")}×)
                        {" "}· Cooldown {Math.round(eff("cooldown_seconds") / 60)}m · max Stage {eff("max_stage")}
                    </div>
                )}

                {!stages ? (
                    <Spinner />
                ) : (
                    <div className="space-y-px bg-zinc-800">
                        {stages.map((s) => {
                            const active = s.stage === currentStage;
                            return (
                                <div
                                    key={s.stage}
                                    className={`flex items-center justify-between p-2 ${active ? "bg-[rgba(0,85,255,0.10)] border-l-2 border-l-[#CCFF00]" : "bg-zinc-950"}`}
                                    data-testid={`autoscaling-stage-${s.stage}`}
                                >
                                    <div className="font-mono text-xs">
                                        <span className={`mr-2 font-bold ${active ? "text-cm-brand" : "text-zinc-300"}`}>
                                            Stage {s.stage}
                                        </span>
                                        <span className="text-zinc-500">
                                            {s.web_vms} VMs · {s.cpu}c/{(s.ram_mb / 1024).toFixed(0)}G · redis {s.redis_type}
                                        </span>
                                    </div>
                                    {!active ? (
                                        <button
                                            onClick={() => scaleTo(s.stage)}
                                            disabled={busy}
                                            className="cm-btn-ghost text-xs flex items-center gap-1 disabled:opacity-40"
                                            data-testid={`autoscaling-scale-to-${s.stage}`}
                                        >
                                            {s.stage > currentStage
                                                ? <ChevronUp size={11} className="text-[#00cc66]" />
                                                : <ChevronDown size={11} className="text-[#ffcc00]" />}
                                            Scale to
                                        </button>
                                    ) : (
                                        <span className="cm-badge cm-badge-info text-[10px]">current</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* --- Scale-Historie (Audit-Trail) --- */}
                <div className="mt-3 pt-3 border-t border-zinc-800" data-testid="scale-history">
                    <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5 flex items-center gap-1">
                        <History size={11} /> Scale history
                    </div>
                    {!history || history.length === 0 ? (
                        <p className="text-xs text-zinc-600" data-testid="scale-history-empty">No scale events yet.</p>
                    ) : (
                        <div className="space-y-1">
                            {history.filter((h) => h.action !== "cloud.autoscale.configure").slice(0, 6).map((h, idx) => (
                                <div key={idx} data-testid={`scale-history-row-${idx}`} className="flex items-start justify-between text-xs bg-zinc-950 border border-zinc-800 px-2 py-1">
                                    <div className="min-w-0">
                                        <span className={`font-mono mr-1.5 ${h.automatic ? "text-cm-brand" : "text-zinc-400"}`}>
                                            {h.automatic ? "AUTO" : "MANU"}
                                        </span>
                                        {h.from_stage != null && h.to_stage != null && (
                                            <span className="font-mono text-zinc-300">
                                                Stage {h.from_stage}→{h.to_stage}
                                            </span>
                                        )}
                                        {h.reason && <span className="block text-zinc-600 truncate">{h.reason}</span>}
                                    </div>
                                    <span className="font-mono text-zinc-600 shrink-0 ml-2">{h.ts ? fmtRel(h.ts) : ""}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function NumField({ label, value, onChange, testid }) {
    return (
        <label className="flex flex-col gap-0.5">
            <span className="text-[11px] text-zinc-500">{label}</span>
            <input
                type="number"
                data-testid={testid}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 px-2 py-1 text-xs font-mono text-zinc-100 focus:border-[#CCFF00] outline-none"
            />
        </label>
    );
}

function Row({ k, v, testid }) {
    return (
        <div className="flex items-center justify-between py-1 border-b border-zinc-800/60">
            <span className="text-xs text-zinc-500">{k}</span>
            <span data-testid={testid} className="text-xs font-mono text-zinc-200">{v}</span>
        </div>
    );
}

function Gauge({ label, value, unit, danger }) {
    return (
        <div className="bg-zinc-950 p-3" data-testid={`metric-gauge-${label.toLowerCase()}`}>
            <div className="cm-label">{label}</div>
            <div className={`font-mono text-2xl mt-1 ${danger ? "text-red-400" : "text-zinc-50"}`}>
                {value.toFixed(label === "REQ" ? 0 : 1)}
                <span className="text-[11px] text-zinc-500 ml-1">{unit}</span>
            </div>
        </div>
    );
}

function Sparkline({ data, field, label }) {
    if (!data?.length) return null;
    const W = 280, H = 28;
    const values = data.map((d) => d[field]);
    const min = Math.min(0, ...values);
    const max = Math.max(...values, field.includes("req") ? 50 : 100);
    const range = Math.max(1, max - min);
    const points = values
        .map((v, i) => {
            const x = (i / Math.max(1, values.length - 1)) * W;
            const y = H - ((v - min) / range) * H;
            return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(" ");
    return (
        <div className="flex items-center gap-3 py-1">
            <div className="cm-label w-16 shrink-0">{label}</div>
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="flex-1 h-7">
                <polyline fill="none" stroke="#CCFF00" strokeWidth="1.5" points={points} />
            </svg>
            <div className="font-mono text-xs text-zinc-400 w-12 text-right">
                {(values[values.length - 1] ?? 0).toFixed(0)}
            </div>
        </div>
    );
}
